import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useRecoilValue } from 'recoil';
import { Button } from '../../../../components/ui';
import { templateFormAtom } from '../../state/templateAtoms';
import { getBodyPreviewText } from '../../utils/templateSendUtils';
import useTemplateSx from '../../hooks/useTemplateSx';

function formatMessage(text) {
  if (!text) return null;

  const elements = [];
  const regex = /(\*[^*]+\*|_[^_]+_)/g;
  let lastIndex = 0;
  const matches = [...text.matchAll(regex)];

  matches.forEach((match, i) => {
    const matchText = match[0];
    const start = match.index;
    if (start > lastIndex) elements.push(text.slice(lastIndex, start));
    if (matchText.startsWith('*') && matchText.endsWith('*')) {
      elements.push(<strong key={`bold-${i}`}>{matchText.slice(1, -1)}</strong>);
    } else if (matchText.startsWith('_') && matchText.endsWith('_')) {
      elements.push(<em key={`italic-${i}`}>{matchText.slice(1, -1)}</em>);
    }
    lastIndex = start + matchText.length;
  });

  if (lastIndex < text.length) elements.push(text.slice(lastIndex));
  return elements;
}

export default function TemplateMessagePreview({ file = null, existingFileUrl = null }) {
  const styles = useTemplateSx();
  const formData = useRecoilValue(templateFormAtom);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!file) {
      setBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const headerComponent = formData?.components?.find((c) => c.type === 'HEADER');
  const headerType = headerComponent?.format;
  const headerText = headerComponent?.text || '';
  const mediaUrl = blobUrl || existingFileUrl || formData?.media?.header || null;
  const footerText = formData?.components?.find((c) => c.type === 'FOOTER')?.text || '';
  const buttons =
    formData?.components?.find((c) => c.type === 'BUTTONS')?.buttons ?? [];

  const renderHeaderContent = () => {
    if (!headerComponent) return null;

    if (headerType === 'IMAGE' && mediaUrl) {
      return (
        <Box sx={{ mb: 1 }}>
          <img
            src={mediaUrl}
            alt="Header"
            style={{
              width: '100%',
              maxHeight: 180,
              borderRadius: 6,
              objectFit: 'cover',
            }}
          />
        </Box>
      );
    }

    if (headerType === 'VIDEO' && mediaUrl) {
      return (
        <Box sx={{ mb: 1 }}>
          <video
            key={mediaUrl}
            controls
            style={{ width: '100%', maxHeight: 200, borderRadius: 6 }}
          >
            <source src={mediaUrl} type={file?.type || 'video/mp4'} />
          </video>
        </Box>
      );
    }

    if (headerType === 'DOCUMENT' && (mediaUrl || file)) {
      const fileName =
        file?.name ||
        (mediaUrl ? decodeURIComponent(mediaUrl.split('/').pop().split('?')[0]) : 'Document');
      return (
        <Box
          sx={{
            mb: 1,
            p: 1,
            bgcolor: 'grey.100',
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <DescriptionOutlinedIcon sx={{ color: '#128C7E', fontSize: 22, flexShrink: 0 }} />
          <Typography
            {...(mediaUrl
              ? {
                  component: 'a',
                  href: mediaUrl,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                }
              : {})}
            variant="body2"
            sx={{
              color: '#128C7E',
              textDecoration: 'none',
              wordBreak: 'break-all',
              fontWeight: 500,
              fontSize: '0.8125rem',
            }}
          >
            {fileName}
          </Typography>
        </Box>
      );
    }

    if (headerType === 'TEXT') {
      return (
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            wordBreak: 'break-word',
            mb: 0.5,
          }}
        >
          {headerText}
        </Typography>
      );
    }

    return null;
  };

  return (
    <Box sx={styles.previewCard}>
      <Box sx={styles.previewCardHeader}>
        <Typography sx={styles.previewCardTitle}>Live Preview</Typography>
        <Typography sx={styles.previewCardSubtitle}>
          WhatsApp message preview
        </Typography>
      </Box>

      <Box sx={styles.previewCardBody}>
        <Typography sx={styles.previewTemplateName}>
          {formData?.name || 'Untitled template'}
        </Typography>

        <Box sx={styles.previewPhoneShell}>
          <Box sx={styles.previewPhoneHeader}>
            <Typography sx={styles.previewPhoneHeaderTitle}>Template message</Typography>
            <Typography sx={styles.previewPhoneHeaderTime}>Now</Typography>
          </Box>

          <Box sx={styles.previewBubble}>
            {renderHeaderContent()}

            <Typography sx={styles.previewBubbleText}>
              {formatMessage(getBodyPreviewText(formData)) || 'Body message not available'}
            </Typography>

            {buttons.length > 0 && (
              <Box sx={styles.previewButtonsRow}>
                {buttons.map((item, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    sx={styles.previewButtonChip}
                  >
                    {item?.text || 'Button'}
                  </Button>
                ))}
              </Box>
            )}

            {footerText ? (
              <Typography sx={styles.previewFooterText}>{formatMessage(footerText)}</Typography>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
